// iOS push notification bootstrap.
//
// Only runs inside a Capacitor build — the dynamic import of
// @capacitor/push-notifications fails on the web, which is exactly
// what we want: web users don't need push, the import error is
// caught silently, and nothing in api.ts has to branch on platform.
//
// Flow on app launch (after the user is signed in):
//   1. Request notification permission (iOS shows the system prompt
//      the first time, then remembers the answer).
//   2. Call register() — Apple returns the device token to the
//      `registration` listener as a hex string.
//   3. Upsert the token into the device_tokens table via the API.
//      The edge function reads from there during fan-out.
//
// We re-run on every sign-in (not just first launch) because the
// token can change — iOS reissues on reinstall, OS upgrade, etc.
// Each call is an upsert keyed on (user_id, token), so dupes are
// just last_seen_at refreshes.
//
// Build-time switch: set VITE_APNS_ENV=sandbox for Xcode debug
// installs (which use api.sandbox.push.apple.com). TestFlight and
// App Store builds always use production — leave it unset there.

import { api } from "./api";

let registered = false;

const APNS_ENV: "sandbox" | "production" =
  (import.meta.env.VITE_APNS_ENV === "sandbox") ? "sandbox" : "production";

// Bundle id has to match `apns-topic`. Pulled from the Capacitor
// config at runtime so we can't drift from what the iOS binary
// thinks it is.
async function getBundleId(): Promise<string | null> {
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return info.id ?? null;
  } catch {
    return null;
  }
}

export async function initPushNotifications(): Promise<void> {
  if (registered) return;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // iOS surfaces a system dialog the first time; subsequent calls
    // resolve immediately with whatever the user picked. If they
    // denied, we never see a token — that's a deliberate user
    // choice, not an error to retry.
    const perm = await PushNotifications.checkPermissions();
    let status = perm.receive;
    if (status === "prompt" || status === "prompt-with-rationale") {
      const req = await PushNotifications.requestPermissions();
      status = req.receive;
    }
    if (status !== "granted") return;

    const bundleId = await getBundleId();
    if (!bundleId) {
      console.warn("[push] could not resolve bundle id; skipping register");
      return;
    }

    // Listener has to be attached BEFORE register() — Apple delivers
    // the token via this event, possibly synchronously.
    await PushNotifications.addListener("registration", async (t) => {
      try {
        await api.registerPushToken({
          token: t.value,
          environment: APNS_ENV,
          bundleId,
        });
      } catch (e) {
        console.warn("[push] registerPushToken failed:", e);
      }
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.warn("[push] registrationError:", err);
    });

    await PushNotifications.register();
    registered = true;
  } catch {
    /* Capacitor / push plugin not present — web build. */
  }
}

// Called from logout so the server stops sending alerts to a
// device that's been signed out of. Best-effort: if the unregister
// fails the row will eventually age out or be revoked on the next
// 410 from APNs.
export async function revokeCurrentDeviceToken(): Promise<void> {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    // The plugin doesn't expose "get my current token" — but we
    // captured it on register and the row in the DB is keyed on it.
    // Cheapest correct thing: ask the plugin to remove all delivered
    // notifications and let the server-side 410 revoke do the rest
    // on the next attempted push.
    await PushNotifications.removeAllDeliveredNotifications();
    registered = false;
  } catch {
    /* not on iOS */
  }
}
